import { ChangeEvent, FC, forwardRef, memo, useCallback, useMemo, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useCombinedRefs } from '@utils';

import getValuesFromOptions from './getValuesFromOptions';
import { CheckboxGroupProps } from './types';

const Component: FC<CheckboxGroupProps> = memo(
    forwardRef((props, ref) => {
        const {
            id,
            values = [],
            onChange,
            options,
            label,
            showSelectAll,
            disabled,
            errorText,
            required,
            validator,
            parentHasError,
            inline,
            showLabel,
        } = props;

        const [builtInErrorMessage, setErrorMessage] = useState(''),
            checkboxGroupId = useMemo(() => id || label, [id, label]),
            checkboxGroupRef = useCombinedRefs<HTMLDivElement>(ref, useRef(null)),
            hasError = useMemo(
                () => !!errorText || !!builtInErrorMessage || parentHasError,
                [builtInErrorMessage, errorText, parentHasError],
            ),
            allChildValues = useMemo(() => getValuesFromOptions(options), [options]),
            areAllValuesSelected = useMemo(() => allChildValues.every((val) => values?.includes(val)), [values, allChildValues]);

        const validate = useCallback(
            (selectedValues) => {
                const message =
                    (validator && validator(selectedValues)) ||
                    (required && selectedValues.length === 0 && 'Please select at least one option.') ||
                    '';
                setErrorMessage(message);
            },
            [validator, required],
        );

        const handleOptionSelection = useCallback(
                (item: any) => (isChecked: boolean) => {
                    const newValues = isChecked ? [...(values ?? []), item] : (values ?? []).filter((vl) => vl !== item);
                    validate(newValues);
                    onChange(newValues);
                },
                [values, onChange, validate],
            ),
            handleOptionClick = useCallback(
                (event: ChangeEvent<HTMLInputElement>) => {
                    event.stopPropagation();
                    const { name, checked } = event.target as HTMLInputElement;
                    handleOptionSelection(name)(checked);
                },
                [handleOptionSelection],
            ),
            handleSelectAllClick = useCallback(() => {
                const newValues = areAllValuesSelected
                    ? (values ?? []).filter((val) => !allChildValues.includes(val))
                    : Array.from(new Set([...(values ?? []), ...allChildValues]));
                validate(newValues);
                onChange(newValues);
            }, [values, onChange, allChildValues, areAllValuesSelected, validate]);

        const indeterminate = useMemo(
            () => !areAllValuesSelected && allChildValues.some((el) => values?.includes(el)),
            [areAllValuesSelected, allChildValues, values],
        );

        return (
            <div ref={checkboxGroupRef} id={`${checkboxGroupId}-wrapper`} aria-describedby={`${checkboxGroupId}-helper-text`}>
                {label &&
                    (showSelectAll ? (
                        <Form.Check
                            key="select-all"
                            label={label}
                            checked={indeterminate || areAllValuesSelected}
                            onChange={handleSelectAllClick}
                            //indeterminate={indeterminate}
                            id={`${checkboxGroupId}-select-all`}
                            //isHovered={cursor === -1}
                            {...{ hasError, disabled }}
                        />
                    ) : showLabel ? (
                        <Form.Label
                            id={`${checkboxGroupId}-label`}
                            type="checkbox"
                            hasError={hasError}
                            required={required}
                            disabled={disabled}
                        >
                            {label}
                        </Form.Label>
                    ) : null)}
                <div id={`${checkboxGroupId}-options`} className={showSelectAll ? 'select-all' : ''}>
                    {options.map((option) => {
                        return Array.isArray(option.value) ? (
                            <Component
                                key={option.label}
                                parentHasError={hasError}
                                id={`${option.label}-${checkboxGroupId}`}
                                {...{
                                    ...props,
                                    showSelectAll: option.showSelectAll ?? true,
                                    options: option.value,
                                    label: option.label,
                                    columns: option.columns,
                                    helperText: option.helperText,
                                    errorText: option.errorText,
                                }}
                            />
                        ) : (
                            <Form.Check
                                key={option.value}
                                name={option.value}
                                label={option.label}
                                checked={values?.includes(option.value)}
                                onChange={handleOptionClick}
                                disabled={disabled || option.disabled}
                                isInvalid={hasError}
                                id={`${option.label}-${checkboxGroupId}`}
                                inline={inline}
                                // isHovered={isHovered && cursor === index}
                                // onSelectionFromKeyboard={handleOptionSelection(option.value)}
                            />
                        );
                    })}
                </div>
            </div>
        );
    }),
);

Component.displayName = 'CheckboxGroup';
export const CheckboxGroup: FC<CheckboxGroupProps> = Object.assign(Component, {});
